// Auth Context for Go Backend OAuth
import { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import type { ReactNode } from "react";
import { setAuthToken, setRefreshToken } from "../api/api";
import { setAuthToken as setApiClientToken, setRefreshToken as setApiClientRefreshToken } from "../api/apiClient";
import { extractUserClaims, type AccessMatrix } from "../utils/jwtUtils";
import { fetchUserPermissions } from "../services/permissionsService";
import { inactivityService } from "../services/inactivityService";
import { resendEmailOTP } from "../services/twoFactorApi";
import { getAccessToken, setAccessToken as setStoredAccessToken, getRefreshToken, setRefreshToken as setStoredRefreshToken, clearTokens } from "../utils/tokenStorage";
import { getCurrentSubdomain, getLoginUrl } from "../utils/subdomain";

// Get API base URL from environment variables
// VITE_PUBLIC_API_BASE_URL should already include /public/v1 (e.g., http://localhost:8000/public/v1)
const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:8000/core/public/v1';
const LOGOUT_ENDPOINT = import.meta.env.VITE_LOGOUT_ENDPOINT || 'http://localhost:8000/core/api/v1/logout';
// Private API base URL for /me endpoint
const PRIVATE_API_BASE_URL = API_BASE_URL.replace('/public/v1', '/api/v1');
// Portal API base URL for portal /me endpoint (used when portal user data enrichment is needed)
// const PORTAL_API_BASE_URL = API_BASE_URL.replace('/public/v1', '/portal/v1');

console.log('🌐 Auth API Base URL:', API_BASE_URL);
console.log('ENV is', import.meta.env)

/**
 * Fetch user data from /me endpoint
 * Returns roles (from nested roles object) and profile data
 */
const fetchUserData = async (): Promise<{
  first_name?: string;
  last_name?: string;
  email?: string;
  is_superadmin?: boolean;
  is_hrmanager?: boolean;      // Global HRBP (is_global_hrbp from roles)
  is_rc_hrbp?: boolean;        // Scoped HRBP (is_scoped_hrbp from roles)
  is_rc_executive?: boolean;   // Executive (is_executive from roles)
  is_rc_manager?: boolean;     // Manager (is_manager from roles)
  is_rc_employee?: boolean;    // Employee (is_employee from roles)
  is_coach?: boolean;           // Coach (is_coach from roles)
  is_coachee?: boolean;         // Coachee (is_coachee from roles)
  profile_pic_url?: string;
  // Customer employee fields
  user_category?: 'employee' | 'customer_employee';
  is_customer_admin?: boolean;
  access?: string[];
  // Account data from /me (includes feature flags like can_manage_customers)
  _account?: Account;
} | null> => {
  try {
    const token = getAccessToken();
    if (!token) {
      console.warn('⚠️ fetchUserData: No access token available');
      return null;
    }

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    };

    // Check for impersonation session and add token
    const impersonationDataStr = localStorage.getItem('impersonation_data');
    if (impersonationDataStr) {
      try {
        const impersonationData = JSON.parse(impersonationDataStr);
        if (impersonationData.sessionToken) {
          headers['X-Impersonation-Token'] = impersonationData.sessionToken;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    const response = await fetch(`${PRIVATE_API_BASE_URL}/me`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      console.warn(`⚠️ fetchUserData: API returned ${response.status}`);
      return null;
    }

    const data = await response.json();
    console.log('🔐 fetchUserData: /me response:', data);

    // Extract profile pic URL
    const profilePicUrl = data.user?.profile_pic_url || undefined;

    // Extract customer employee fields from user and account
    const userCategory = data.user?.user_category as 'employee' | 'customer_employee' | 'policyholder' | undefined;
    const isCustomerAdmin = data.user?.is_customer_admin ?? false;
    const customerAccess = data.account?.access || [];

    console.log('🔐 fetchUserData: Customer employee fields:', {
      user_category: userCategory,
      is_customer_admin: isCustomerAdmin,
      access: customerAccess,
    });

    // Get roles from nested roles object - REQUIRED
    const roles = data.user?.roles || data.roles;

    if (!roles) {
      console.error('❌ fetchUserData: Missing roles object in /me response - this is a critical error');
      return null;
    }

    // Map from roles object only - no fallback to top-level or JWT
    return {
      first_name: data.user?.first_name,
      last_name: data.user?.last_name,
      email: data.user?.email,
      is_superadmin: roles.is_superadmin ?? false,
      is_hrmanager: roles.is_global_hrbp ?? false,     // Global HRBP
      is_rc_hrbp: roles.is_scoped_hrbp ?? false,       // Scoped HRBP (includes HR Heads)
      is_rc_executive: roles.is_executive ?? false,    // Executive (standalone)
      is_rc_manager: roles.is_manager ?? false,        // Manager (separate from executive)
      is_rc_employee: roles.is_employee ?? false,      // Employee
      is_coach: roles.is_coach ?? false,               // Coach
      is_coachee: roles.is_coachee ?? false,           // Coachee
      profile_pic_url: profilePicUrl,
      // Customer employee fields
      user_category: userCategory,
      is_customer_admin: isCustomerAdmin,
      access: customerAccess,
      // Account data (includes feature flags like can_manage_customers)
      // Normalize: API returns company_name, frontend expects name
      _account: {
        ...data.account,
        name: data.account?.name || data.account?.company_name || data.account?.account_name || '',
      },
    };
  } catch (error) {
    console.warn('⚠️ fetchUserData: Failed:', error);
    return null;
  }
};

export interface User {
  user_uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  sub: string;
  profile_pic_url?: string;    // From /me endpoint
  // RBAC role flags - from /me endpoint roles object
  is_superadmin?: boolean;     // SUPERADMIN - master role, no restrictions
  is_hrmanager?: boolean;      // Global HRBP (is_global_hrbp from roles)
  is_rc_hrbp?: boolean;        // Scoped HRBP - includes HR Heads (is_scoped_hrbp from roles)
  is_rc_executive?: boolean;   // Executive - standalone flag (is_executive from roles)
  is_rc_manager?: boolean;     // Manager - separate from executive (is_manager from roles)
  is_rc_employee?: boolean;    // Employee (is_employee from roles)
  is_coach?: boolean;           // Coach (is_coach from roles)
  is_coachee?: boolean;         // Coachee (is_coachee from roles)
  is_admin?: boolean;
  role?: string;
  permissions?: string[];
  access_matrix?: AccessMatrix | null;
  // Customer employee specific fields
  user_category?: 'employee' | 'customer_employee';
  is_customer_admin?: boolean;
  access?: string[]; // Module access: ['LMS', 'TMS', 'Coaching']
}

interface Account {
  account_uuid: string;
  name: string;
  domain: string;
  timezone?: string;
  min_age?: number;
  can_manage_customers?: boolean;
  customer_label?: string;
  access?: string[]; // Module access: ['LMS', 'TMS', 'Coaching']
  company_name?: string;
  account_name?: string;
  organization_name?: string;
  account_domain?: string;
}

// Impersonation types
interface ImpersonationData {
  sessionToken: string;
  sessionId: string;
  expiresAt: Date;
  reason: string;
  adminToken: string;
}

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isRolesLoading: boolean;
  user: User | null;
  account: Account | null;
  token: string | null;
  isImpersonating: boolean;
  impersonationData: ImpersonationData | null;
  loginWithGoogle: (redirect?: string) => void;
  registerWithGoogle: () => void;
  loginWithMicrosoft: (redirect?: string) => void;
  loginWithCredentials: (unique_id: string, password: string, verification_id?: string, otp_code?: string, org_id?: string, domain?: string) => Promise<{
    success: boolean;
    error?: string;
    code?: string;
    verificationRequired?: boolean;
    session_id?: string;
    user?: any;
    setup_required?: boolean;
    otp_required?: boolean;
  }>;
  verifyOtpWithSession: (sessionId: string, otpCode: string) => Promise<{
    success: boolean;
    error?: string;
    code?: string;
  }>;
  checkAuthMethod: (unique_id: string, org_id?: string, domain?: string) => Promise<{
    success: boolean;
    authMethod?: string;
    error?: string;
    code?: string;
    exists?: boolean;
    multiple_users?: boolean;
    route_to_reset_password?: boolean;
    is_admin?: boolean;
    email?: boolean;
    user_id?: boolean;
    client_id?: boolean;
    message?: string;
    reset_token?: string;
    user_uuid?: number;
  }>;
  adminCheckAuth: (admin_user_id: string, email: string) => Promise<{ success: boolean; error?: string }>;
  adminLoginWithCredentials: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  setAuthData: (data: any) => void;
  updateAccount: (updates: Partial<Account>) => void;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
  startImpersonation: (params: {
    sessionToken: string;
    sessionId: string;
    expiresAt: string;
    reason: string;
    adminToken: string;
  }) => Promise<{ success: boolean; error?: string }>;
  endImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeAccount = (rawAccount: any): Account | null => {
  if (!rawAccount || typeof rawAccount !== 'object') return null;

  const account_uuid = rawAccount.account_uuid ?? rawAccount.id ?? rawAccount.account_id ?? '';
  const name = rawAccount.company_name || rawAccount.account_name || rawAccount.name || rawAccount.organization_name || '';
  const domain = rawAccount.domain || rawAccount.account_domain || '';

  return {
    ...rawAccount,
    account_uuid: account_uuid ? String(account_uuid) : '',
    name,
    domain,
  } as Account;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null);

  // Ref to prevent duplicate fetches during rapid state changes
  const isFetchingUserData = useRef(false);

  // Re-fetch /me to refresh user data (called after profile updates)
  const refreshUser = useCallback(async () => {
    try {
      const userData = await fetchUserData();
      if (userData) {
        setUser((prev) => {
          const updatedUser = {
            ...prev,
            ...userData,
            first_name: userData.first_name ?? prev?.first_name ?? '',
            last_name: userData.last_name ?? prev?.last_name ?? '',
            email: userData.email ?? prev?.email ?? '',
            profile_pic_url: userData.profile_pic_url ?? prev?.profile_pic_url,
          } as User;
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }
    } catch (e) {
      console.warn('Failed to refresh user from /me:', e);
    }
  }, []);

  // Listen for profile updates — patch user state directly (no /me refetch, no heavy re-render)
  useEffect(() => {
    const handleAuthUserPatched = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setUser((prev) => prev ? { ...prev, ...detail } : prev);
      }
    };
    window.addEventListener('auth-user-patched', handleAuthUserPatched);
    return () => window.removeEventListener('auth-user-patched', handleAuthUserPatched);
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 Initializing auth from storage...');

      // Skip normal auth flow if we're on the impersonation callback route
      // The callback will handle setting up the impersonation session
      if (window.location.pathname.includes('/impersonation/callback')) {
        console.log('👤 On impersonation callback route, skipping normal auth init...');
        setIsLoading(false);
        setIsRolesLoading(false);
        return;
      }

      // One-time purge of legacy cross-domain cookies from the earlier tokenStorage
      // cookie-mirror bug (caused 431 due to doubled headers). Safe no-op otherwise.
      if (!localStorage.getItem('legacy_cookie_purged')) {
        try {
          const baseDomain = window.location.hostname.endsWith('traineryhcm.com') ? '.traineryhcm.com' : '';
          if (baseDomain) {
            document.cookie = `access_token=; path=/; domain=${baseDomain}; max-age=0`;
            document.cookie = `refresh_token=; path=/; domain=${baseDomain}; max-age=0`;
          }
        } catch { /* ignore */ }
        localStorage.setItem('legacy_cookie_purged', '1');
      }

      // Subdomain handoff: tokens may arrive in URL fragment from main-domain login.
      // Consume them before reading storage, then strip hash so tokens don't linger in URL.
      if (getCurrentSubdomain() && window.location.hash) {
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        const params = new URLSearchParams(hash);
        const handoffAccess = params.get('access_token');
        const handoffRefresh = params.get('refresh_token');
        if (handoffAccess) {
          setStoredAccessToken(handoffAccess);
          if (handoffRefresh) setStoredRefreshToken(handoffRefresh);
          // Strip the hash from the URL so tokens don't remain in browser history/UI
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }

      const storedAccessToken = getAccessToken();
      const storedRefreshToken = getRefreshToken();
      const storedUser = localStorage.getItem('user');
      const storedAccount = localStorage.getItem('account');
      const storedImpersonationData = localStorage.getItem('impersonation_data');

      // Check if this is an impersonation session
      if (storedImpersonationData) {
        try {
          const impData = JSON.parse(storedImpersonationData);
          const expiresAt = new Date(impData.expiresAt);

          // Check if session is still valid
          if (expiresAt > new Date()) {
            console.log('👤 Restoring impersonation session...');
            setIsImpersonating(true);
            setImpersonationData({
              sessionToken: impData.sessionToken,
              sessionId: impData.sessionId,
              expiresAt,
              reason: impData.reason,
              adminToken: impData.adminToken,
            });

            if (storedUser && storedUser !== 'undefined') {
              setUser(JSON.parse(storedUser));
            }
            if (storedAccount && storedAccount !== 'undefined') {
              const parsedAccount = JSON.parse(storedAccount);
              const normalizedAccount = normalizeAccount(parsedAccount);
              setAccount(normalizedAccount);
              if (normalizedAccount) {
                localStorage.setItem('account', JSON.stringify(normalizedAccount));
              }
            }
            setToken(impData.adminToken);
            setAuthToken(impData.adminToken);
            setIsAuthenticated(true);
            setIsLoading(false);
            setIsRolesLoading(false); // Impersonation has roles from /me already
            console.log('✅ Impersonation session restored');
            return;
          } else {
            // Session expired, clear impersonation data
            console.log('⚠️ Impersonation session expired, clearing...');
            localStorage.removeItem('impersonation_data');
          }
        } catch (error) {
          console.error('❌ Error restoring impersonation session:', error);
          localStorage.removeItem('impersonation_data');
        }
      }

      if (storedAccessToken && storedUser && storedUser !== 'undefined') {
        console.log('✅ Found stored tokens');

        try {
          setAuthToken(storedAccessToken);
          if (storedRefreshToken && storedRefreshToken !== 'undefined') {
            setRefreshToken(storedRefreshToken);
          }
          setToken(storedAccessToken);

          // Parse user and normalize user_uuid to string
          const parsedUser = JSON.parse(storedUser) as User;
          console.log('🔍 Parsed user from localStorage:', parsedUser);
          console.log('🔍 user_uuid type:', typeof parsedUser.user_uuid);

          // CRITICAL: Ensure user_uuid is always a string to prevent precision loss
          if (parsedUser.user_uuid !== undefined) {
            const originalUuid = parsedUser.user_uuid;
            parsedUser.user_uuid = String(parsedUser.user_uuid);
            console.log('🔄 Normalized user_uuid:', {
              original: originalUuid,
              originalType: typeof originalUuid,
              normalized: parsedUser.user_uuid,
              normalizedType: typeof parsedUser.user_uuid
            });
          }

          // Extract identity claims from JWT (name fields only)
          // NOTE: RBAC flags are NOT in JWT - they are fetched from /me API
          const jwtClaims = extractUserClaims(storedAccessToken);
          if (jwtClaims) {
            // Merge name fields from JWT if not present in stored user
            parsedUser.first_name = parsedUser.first_name || jwtClaims.first_name || '';
            parsedUser.last_name = parsedUser.last_name || jwtClaims.last_name || '';
            console.log('🔓 Restored JWT identity claims on init:', {
              first_name: parsedUser.first_name,
              last_name: parsedUser.last_name,
            });
            console.log('ℹ️ RBAC flags will be fetched from /me API');
          }

          // Set user IMMEDIATELY with JWT/cached data (so app works while API loads)
          setUser(parsedUser);
          if (storedAccount && storedAccount !== 'undefined') {
            const parsedAccount = JSON.parse(storedAccount);
            const normalizedAccount = normalizeAccount(parsedAccount);
            setAccount(normalizedAccount);
            if (normalizedAccount) {
              localStorage.setItem('account', JSON.stringify(normalizedAccount));
            }
          }
          setIsAuthenticated(true);
          console.log('✅ Auth restored successfully');

          // Skip fetching permissions/roles if impersonation data exists
          // (impersonation should have been handled above, but this is a safety check)
          const hasImpersonationData = !!localStorage.getItem('impersonation_data');
          if (hasImpersonationData) {
            console.log('👤 Skipping fetchUserData/fetchUserPermissions - impersonation detected');
            setIsRolesLoading(false);
          } else {
            // Prevent duplicate fetches during rapid state changes
            if (isFetchingUserData.current) {
              console.log('⏳ User data fetch already in progress, skipping duplicate');
              return;
            }
            isFetchingUserData.current = true;

            // Start roles loading - sidebar should wait for this
            setIsRolesLoading(true);

            // Fetch permissions and user data in parallel, then merge in a single state update
            Promise.all([
              fetchUserPermissions().catch((err) => {
                console.warn('⚠️ Could not refresh API permissions on init:', err);
                return null;
              }),
              fetchUserData().catch((err) => {
                console.warn('⚠️ Could not refresh user data on init:', err);
                return null;
              })
            ]).then(([apiPermissions, userData]) => {
              // Get current user from localStorage to merge with fresh data
              const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
              const updatedUser = {
                ...currentUser,
                // Merge permissions — use {} if fetch failed so guards know loading is done
                access_matrix: apiPermissions || currentUser.access_matrix || {},
                // Merge user data if available
                ...(userData && {
                  // Profile fields from /me (source of truth over JWT)
                  first_name: userData.first_name ?? currentUser.first_name,
                  last_name: userData.last_name ?? currentUser.last_name,
                  email: userData.email ?? currentUser.email,
                  is_superadmin: userData.is_superadmin ?? currentUser.is_superadmin,
                  is_hrmanager: userData.is_hrmanager ?? currentUser.is_hrmanager,
                  is_rc_hrbp: userData.is_rc_hrbp ?? currentUser.is_rc_hrbp,
                  is_rc_manager: userData.is_rc_manager ?? currentUser.is_rc_manager,
                  is_rc_employee: userData.is_rc_employee ?? currentUser.is_rc_employee,
                  is_rc_executive: userData.is_rc_executive ?? currentUser.is_rc_executive,
                  is_coach: userData.is_coach ?? currentUser.is_coach,
                  is_coachee: userData.is_coachee ?? currentUser.is_coachee,
                  profile_pic_url: userData.profile_pic_url ?? currentUser.profile_pic_url,
                  // Customer employee fields from /me API
                  user_category: userData.user_category ?? currentUser.user_category,
                  is_customer_admin: userData.is_customer_admin ?? currentUser.is_customer_admin,
                  access: userData.access ?? currentUser.access,
                }),
              };

              // Single state update with all data
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));

              // Update account from /me response (includes feature flags like can_manage_customers)
              if (userData?._account) {
                setAccount(userData._account);
                localStorage.setItem('account', JSON.stringify(userData._account));
              }

              console.log('✅ Refreshed user data on init (coordinated):', {
                hasPermissions: !!apiPermissions,
                is_superadmin: updatedUser.is_superadmin,
                is_hrmanager: updatedUser.is_hrmanager,
                is_rc_hrbp: updatedUser.is_rc_hrbp,
                is_rc_manager: updatedUser.is_rc_manager,
                is_rc_employee: updatedUser.is_rc_employee,
                is_rc_executive: updatedUser.is_rc_executive,
                profile_pic_url: updatedUser.profile_pic_url,
                user_category: updatedUser.user_category,
                is_customer_admin: updatedUser.is_customer_admin,
                access: updatedUser.access,
                can_manage_customers: userData?._account?.can_manage_customers,
              });
            }).finally(() => {
              // Roles loading complete (whether success or failure)
              isFetchingUserData.current = false;
              setIsRolesLoading(false);
              console.log('🔄 Roles loading complete');
            });
          }
        } catch (error) {
          console.error('❌ Error restoring auth:', error);
          // Don't immediately clear - let auth flow handle it
          setIsAuthenticated(false);
          setIsRolesLoading(false);
        }
      } else if (storedAccessToken && getCurrentSubdomain()) {
        // Subdomain bootstrap: token just arrived via URL fragment handoff (see above),
        // but no user/account is in localStorage yet (localStorage is origin-specific).
        // Fetch /me to reconstruct.
        console.log('🔄 Subdomain bootstrap: token from URL handoff, fetching /me...');
        try {
          setAuthToken(storedAccessToken);
          if (storedRefreshToken && storedRefreshToken !== 'undefined') {
            setRefreshToken(storedRefreshToken);
          }
          setToken(storedAccessToken);
          setIsRolesLoading(true);

          const userData = await fetchUserData();
          if (userData && userData._account) {
            // Reconstruct user object from /me response
            const jwtClaims = extractUserClaims(storedAccessToken);
            const bootstrappedUser: User = {
              user_uuid: String(jwtClaims?.user_uuid || ''),
              email: jwtClaims?.email || '',
              first_name: jwtClaims?.first_name || '',
              last_name: jwtClaims?.last_name || '',
              sub: jwtClaims?.sub || '',
              is_superadmin: userData.is_superadmin,
              is_hrmanager: userData.is_hrmanager,
              is_rc_hrbp: userData.is_rc_hrbp,
              is_rc_executive: userData.is_rc_executive,
              is_rc_manager: userData.is_rc_manager,
              is_rc_employee: userData.is_rc_employee,
              profile_pic_url: userData.profile_pic_url,
              user_category: userData.user_category,
              is_customer_admin: userData.is_customer_admin,
              access: userData.access,
            };

            // Store in localStorage on this subdomain origin
            localStorage.setItem('user', JSON.stringify(bootstrappedUser));
            setUser(bootstrappedUser);

            const normalizedAccount = normalizeAccount(userData._account);
            if (normalizedAccount) {
              localStorage.setItem('account', JSON.stringify(normalizedAccount));
            }
            setAccount(normalizedAccount);

            setIsAuthenticated(true);
            console.log('✅ Subdomain bootstrap complete - user authenticated from /me');

            // Fetch permissions in background
            fetchUserPermissions().then((apiPermissions) => {
              const updatedUser = { ...bootstrappedUser, access_matrix: apiPermissions || {} };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }).catch((err) => {
              console.warn('⚠️ Could not refresh permissions after subdomain bootstrap:', err);
              // Set empty access_matrix so guards know permissions loaded (but failed)
              const updatedUser = { ...bootstrappedUser, access_matrix: {} };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }).finally(() => {
              setIsRolesLoading(false);
            });
          } else {
            // /me failed - token might be invalid, clear and mark failed
            console.warn('⚠️ Subdomain bootstrap failed: /me returned no data');
            clearTokens();
            sessionStorage.setItem('subdomain_bootstrap_failed', '1');
            setIsRolesLoading(false);
          }
        } catch (error) {
          console.error('❌ Subdomain bootstrap error:', error);
          clearTokens();
          sessionStorage.setItem('subdomain_bootstrap_failed', '1');
          setIsRolesLoading(false);
        }
      } else {
        console.log('ℹ️ No stored tokens found');
        setIsRolesLoading(false); // No auth = no roles to load
      }

      setIsLoading(false);
    };

    initAuth().catch((err) => {
      console.error('❌ Unexpected error in initAuth:', err);
      setIsLoading(false);
      setIsRolesLoading(false);
    });
  }, []);

  // Initialize inactivity service when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      console.log('🕒 Initializing inactivity timeout service');
      inactivityService.initialize();
    } else if (!isAuthenticated && !isLoading) {
      console.log('🕒 Cleaning up inactivity timeout service');
      inactivityService.cleanup();
    }

    // Cleanup on unmount
    return () => {
      if (inactivityService.isInitialized()) {
        inactivityService.cleanup();
      }
    };
  }, [isAuthenticated, isLoading]);

  // Helper function to save auth data to state and localStorage
  const setAuthData = (data: any) => {
    console.log('💾 Saving auth data:', data);

    // Clear subdomain bootstrap failure flag on successful login
    sessionStorage.removeItem('subdomain_bootstrap_failed');

    // Save tokens IMMEDIATELY in BOTH API clients (api.ts and apiClient.ts)
    setAuthToken(data.access_token);
    setRefreshToken(data.refresh_token);
    setApiClientToken(data.access_token);  // Also update apiClient.ts token
    setApiClientRefreshToken(data.refresh_token);  // Also update apiClient.ts refresh token
    setToken(data.access_token);
    setStoredAccessToken(data.access_token);
    setStoredRefreshToken(data.refresh_token);

    // Decode JWT to extract identity claims (name fields only)
    // NOTE: RBAC flags are NOT in JWT - they are fetched from /me API
    const jwtClaims = extractUserClaims(data.access_token);
    console.log('🔓 Extracted JWT identity claims:', jwtClaims);

    // Merge user data from response with JWT claims
    const normalizedUser = { ...data.user } as User;

    // Debug: Log what we got from API
    console.log('🔍 Raw data.user from API:', {
      user_category: data.user?.user_category,
      is_customer_admin: data.user?.is_customer_admin,
    });

    // CRITICAL: Normalize user_uuid to string before storing
    if (normalizedUser.user_uuid !== undefined) {
      const originalUuid = normalizedUser.user_uuid;
      normalizedUser.user_uuid = String(normalizedUser.user_uuid);
      console.log('🔄 Normalized user_uuid in setAuthData:', {
        original: originalUuid,
        originalType: typeof originalUuid,
        normalized: normalizedUser.user_uuid,
        normalizedType: typeof normalizedUser.user_uuid,
        lengthCheck: normalizedUser.user_uuid.length
      });
    }

    // Add identity claims from JWT (name fields only)
    // RBAC flags will be fetched from /me API
    if (jwtClaims) {
      // Merge name fields from JWT if not present in user object
      normalizedUser.first_name = normalizedUser.first_name || jwtClaims.first_name || '';
      normalizedUser.last_name = normalizedUser.last_name || jwtClaims.last_name || '';

      console.log('👤 User identity from JWT:', {
        first_name: normalizedUser.first_name,
        last_name: normalizedUser.last_name,
      });
      console.log('ℹ️ RBAC flags will be fetched from /me API');
    }

    // Initialize RBAC flags to defaults - they will be updated by fetchUserData from /me API
    normalizedUser.is_superadmin = false;
    normalizedUser.is_hrmanager = false;      // Global HRBP (is_global_hrbp)
    normalizedUser.is_rc_hrbp = false;        // Scoped HRBP (is_scoped_hrbp)
    normalizedUser.is_rc_executive = false;   // Executive (is_executive)
    normalizedUser.is_rc_manager = false;     // Manager (is_manager)
    normalizedUser.is_rc_employee = true;     // Employee - default to true

    // Customer employee fields from login API response
    normalizedUser.user_category = normalizedUser.user_category || 'employee';
    normalizedUser.is_customer_admin = normalizedUser.is_customer_admin || false;

    // Get access array from account (for customer employees)
    // This determines which modules (LMS, TMS, Coaching) the customer has access to
    if (data.account?.access && Array.isArray(data.account.access)) {
      normalizedUser.access = data.account.access;
      console.log('📦 Customer access from account:', data.account.access);
    }

    // Save user and account IMMEDIATELY (with JWT permissions as fallback)
    // Normalize: API returns company_name, frontend expects name
    const normalizedAccount = data.account ? {
      ...data.account,
      name: data.account.name || data.account.company_name || data.account.account_name || '',
    } : data.account;
    setUser(normalizedUser);
    setAccount(normalizedAccount);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.setItem('account', JSON.stringify(normalizedAccount));

    console.log('✅ Auth data saved successfully');

    // Prevent duplicate fetches during rapid state changes
    if (isFetchingUserData.current) {
      console.log('⏳ User data fetch already in progress, skipping duplicate in setAuthData');
      return;
    }
    isFetchingUserData.current = true;

    // Start roles loading - sidebar should wait for this
    setIsRolesLoading(true);

    // Fetch permissions and user data in parallel, then merge in a single state update
    Promise.all([
      fetchUserPermissions().catch((err) => {
        console.warn('⚠️ Could not fetch API permissions, using JWT if available:', err);
        return null;
      }),
      fetchUserData().catch((err) => {
        console.warn('⚠️ Could not fetch user data:', err);
        return null;
      })
    ]).then(([apiPermissions, userData]) => {
      // Get current user from localStorage to merge with fresh data
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...currentUser,
        // Merge permissions — use {} if fetch failed so guards know loading is done
        access_matrix: apiPermissions || currentUser.access_matrix || {},
        // Merge user data if available
        ...(userData && {
          is_superadmin: userData.is_superadmin ?? currentUser.is_superadmin,
          is_hrmanager: userData.is_hrmanager ?? currentUser.is_hrmanager,
          is_rc_hrbp: userData.is_rc_hrbp ?? currentUser.is_rc_hrbp,
          is_rc_manager: userData.is_rc_manager ?? currentUser.is_rc_manager,
          is_rc_employee: userData.is_rc_employee ?? currentUser.is_rc_employee,
          is_rc_executive: userData.is_rc_executive ?? currentUser.is_rc_executive,
          is_coach: userData.is_coach ?? currentUser.is_coach,
          is_coachee: userData.is_coachee ?? currentUser.is_coachee,
          profile_pic_url: userData.profile_pic_url ?? currentUser.profile_pic_url,
          // Customer employee fields from /me API
          user_category: userData.user_category ?? currentUser.user_category,
          is_customer_admin: userData.is_customer_admin ?? currentUser.is_customer_admin,
          access: userData.access ?? currentUser.access,
        }),
      };

      // Single state update with all data
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update account from /me response (includes feature flags like can_manage_customers)
      if (userData?._account) {
        setAccount(userData._account);
        localStorage.setItem('account', JSON.stringify(userData._account));
      }

      console.log('✅ Updated with fresh user data (coordinated):', {
        hasPermissions: !!apiPermissions,
        is_superadmin: updatedUser.is_superadmin,
        is_hrmanager: updatedUser.is_hrmanager,
        is_rc_hrbp: updatedUser.is_rc_hrbp,
        is_rc_manager: updatedUser.is_rc_manager,
        is_rc_employee: updatedUser.is_rc_employee,
        is_rc_executive: updatedUser.is_rc_executive,
        profile_pic_url: updatedUser.profile_pic_url,
        user_category: updatedUser.user_category,
        is_customer_admin: updatedUser.is_customer_admin,
        access: updatedUser.access,
        can_manage_customers: userData?._account?.can_manage_customers,
      });
    }).finally(() => {
      // Roles loading complete (whether success or failure)
      isFetchingUserData.current = false;
      setIsRolesLoading(false);
      console.log('🔄 Roles loading complete after setAuthData');
    });
  };

  // Update account data (e.g., timezone)
  const updateAccount = (updates: Partial<Account>) => {
    if (!account) return;

    const updatedAccount = { ...account, ...updates };
    setAccount(updatedAccount);
    localStorage.setItem('account', JSON.stringify(updatedAccount));
    console.log('✅ Account updated:', updates);
  };

  // Google SSO Login - Call backend to get auth URL (optional redirect for post-login navigation)
  const loginWithGoogle = async (redirect?: string) => {
    console.log('🔐 Starting Google SSO Login');

    try {
      let url = `${API_BASE_URL}/account/google-login`;
      if (redirect) {
        url += `?redirect=${encodeURIComponent(redirect)}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to get auth URL');
      }

      const data = await response.json();
      console.log('✅ Received auth URL from backend');

      // Store state for CSRF verification
      sessionStorage.setItem('oauth_state', data.state);
      sessionStorage.setItem('oauth_flow', 'login');
      sessionStorage.setItem('oauth_provider', 'google');

      // Redirect to Keycloak OAuth URL
      console.log(`🚀 Redirecting to: ${data.auth_url}`);
      window.location.href = data.auth_url;
    } catch (error) {
      console.error('❌ Error initiating Google SSO:', error);
    }
  };

  // Google SSO Registration - Call backend to get auth URL
  const registerWithGoogle = async () => {
    console.log('🔐 Starting Google SSO Registration');

    try {
      const response = await fetch(`${API_BASE_URL}/account/google-register`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to get auth URL');
      }

      const data = await response.json();
      console.log('✅ Received auth URL from backend');

      // Store state for CSRF verification
      sessionStorage.setItem('oauth_state', data.state);
      sessionStorage.setItem('oauth_flow', 'register');
      sessionStorage.setItem('oauth_provider', 'google');

      // Redirect to Keycloak OAuth URL
      console.log(`🚀 Redirecting to: ${data.auth_url}`);
      window.location.href = data.auth_url;
    } catch (error) {
      console.error('❌ Error initiating Google Registration:', error);
    }
  };

  // Microsoft SSO Login - Call backend to get auth URL (optional redirect for post-login navigation)
  const loginWithMicrosoft = async (redirect?: string) => {
    console.log('🔐 Starting Microsoft SSO Login');

    try {
      let url = `${API_BASE_URL}/account/microsoft-login`;
      if (redirect) {
        url += `?redirect=${encodeURIComponent(redirect)}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to get Microsoft auth URL');
      }

      const data = await response.json();
      console.log('✅ Received Microsoft auth URL from backend');

      // Store state for CSRF verification
      sessionStorage.setItem('oauth_state', data.state);
      sessionStorage.setItem('oauth_flow', 'login');
      sessionStorage.setItem('oauth_provider', 'microsoft');

      // Redirect to Microsoft OAuth URL
      console.log(`🚀 Redirecting to: ${data.auth_url}`);
      window.location.href = data.auth_url;
    } catch (error) {
      console.error('❌ Error initiating Microsoft SSO:', error);
    }
  };

  // Check authentication method for unique_id
  const checkAuthMethod = async (unique_id: string, org_id?: string, domain?: string) => {
    try {
      console.log('🔍 Checking auth method for:', unique_id, org_id ? `(Org: ${org_id})` : '', domain ? `(Domain: ${domain})` : '');

      const payload: Record<string, string> = { unique_id };
      if (org_id) payload.org_id = org_id;
      if (domain) payload.domain = domain;

      const response = await fetch(`${API_BASE_URL}/account/check-auth-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Try to parse JSON response even if response is not ok
      let data;
      try {
        const responseText = await response.text();
        if (!responseText) {
          return {
            success: false,
            error: `Server error (${response.status}): Empty response`,
          };
        }

        // Check if response is HTML instead of JSON
        if (responseText.trim().startsWith('<')) {
          console.error('❌ Received HTML instead of JSON. API URL might be incorrect or endpoint does not exist.');
          console.error('API URL:', `${API_BASE_URL}/account/check-auth-method`);
          console.error('Response preview:', responseText.substring(0, 200));
          return {
            success: false,
            error: 'Invalid email or user ID',
          };
        }

        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Failed to parse response:', parseError);
        return {
          success: false,
          error: response.status >= 500
            ? `Server error (${response.status}): Please try again later`
            : `Invalid server response (${response.status})`,
        };
      }

      if (!response.ok && !data.exists && !data.success) {
        console.log('⚠️ Check auth method failed (non-200):', data.message || data.error);
        return {
          success: false,
          exists: false,
          error: data.message || data.error || 'Failed to check auth method',
          code: data.code,
          ...data // Pass through all new flags
        };
      }

      // Check if user doesn't exist (backend may return 200 with exists=false)
      if (data.exists === false && !data.success) {
        console.log('⚠️ User does not exist:', data.message || data.error);
        return {
          success: false,
          exists: false,
          error: data.message || 'Invalid email or user ID',
          code: data.code,
          ...data
        };
      }

      // Check for collision BEFORE marking as success
      if (data.code === 'REQUIRE_ORG_ID' || data.multiple_users) {
        console.log('⚠️ Collision detected - multiple accounts found');
        return {
          success: false,
          code: 'REQUIRE_ORG_ID',
          ...data
        };
      }

      console.log('✅ Auth method check successful:', data.access_by);
      return {
        success: true,
        authMethod: data.access_by,
        ...data // Pass through all new flags (exists, user_id, client_id, etc.)
      };
    } catch (error) {
      console.error('❌ Check auth method error:', error);
      // Only show generic network error for actual network failures
      return {
        success: false,
        error: error instanceof TypeError && error.message.includes('fetch')
          ? 'Network error or server unavailable'
          : 'An unexpected error occurred. Please try again.',
      };
    }
  };

  // Admin: Check admin credentials (admin_user_id + email)
  const adminCheckAuth = async (admin_user_id: string, email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/check-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ admin_user_id, email }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, error: data.error || 'Failed to verify admin credentials' };
      }
      return { success: true };
    } catch (error) {
      console.error('Admin check auth error:', error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  };

  // Admin: Login with email + password (stores admin_ prefixed tokens)
  const adminLoginWithCredentials = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, error: data.error || data.message || 'Admin login failed' };
      }
      // Store tokens with admin_ prefix (isolated from regular user tokens)
      localStorage.setItem('admin_access_token', data.access_token);
      localStorage.setItem('admin_refresh_token', data.refresh_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin));
      return { success: true };
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: 'An unexpected error occurred. Please try again.' };
    }
  };

  // Email/Password login
  const loginWithCredentials = async (unique_id: string, password: string, verification_id?: string, otp_code?: string, org_id?: string, domain?: string) => {
    try {
      // Structure payload based on whether verification_id and otp_code are provided
      const payload: any = verification_id
        ? { unique_id, verification_id, password } // BOTH case
        : { unique_id, password }; // EMAIL or USERID case

      // Add OTP code if provided (for 2FA verification)
      if (otp_code) {
        payload.otp_code = otp_code;
      }

      // Add Org ID if provided (for collision resolution)
      if (org_id) {
        payload.org_id = org_id;
      }

      // Add domain if provided (for subdomain-based tenant resolution)
      if (domain) {
        payload.domain = domain;
      }

      console.log('🔐 Login payload:', { ...payload, password: '***', otp_code: otp_code ? '***' : undefined });

      // Fetch login response
      const response = await fetch(`${API_BASE_URL}/account/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Workaround: Wrap large numeric IDs in quotes before JSON.parse to prevent precision loss
      const responseText = await response.text();

      // Try to parse as JSON first, then fall back to HTML check
      let data: any;
      try {
        const fixedText = responseText
          .replace(/"user_uuid":\s*(\d{15,})/g, '"user_uuid": "$1"')
          .replace(/"account_uuid":\s*(\d{15,})/g, '"account_uuid": "$1"')
          .replace(/"user_id":\s*(\d{15,})/g, '"user_id": "$1"')
          .replace(/"account_id":\s*(\d{15,})/g, '"account_id": "$1"');
        data = JSON.parse(fixedText);
      } catch {
        // JSON parse failed — check if response is HTML
        if (responseText.trim().startsWith('<')) {
          console.error('❌ Received HTML instead of JSON during login. API URL might be incorrect.');
          console.error('API URL:', `${API_BASE_URL}/account/login`);
          console.error('Response preview:', responseText.substring(0, 200));
        }
        // For non-OK responses, return a status-specific error
        if (response.status === 403) {
          return {
            success: false,
            error: 'Your account plan has expired. Please contact your administrator.',
            code: 'ACCOUNT_RELATIONSHIP_INACTIVE',
          };
        }
        return {
          success: false,
          error: 'Unable to connect to authentication server. Please try again.',
        };
      }

      if (!response.ok || !data.success) {
        // Check for 2FA setup required (NEW FLOW - session_id based)
        if (data.code === '2FA_SETUP_REQUIRED' || data.setup_required) {
          console.log('🔐 2FA setup required - storing session_id for setup flow');

          // Clear any old session_id first
          localStorage.removeItem('2fa_session_id');

          // Store NEW session_id temporarily for 2FA setup
          if (data.session_id) {
            localStorage.setItem('2fa_session_id', data.session_id);
            console.log('🔐 Stored NEW session_id:', data.session_id);
          } else {
            console.error('❌ No session_id provided in 2FA_SETUP_REQUIRED response');
          }

          // Store user data if provided (for display purposes only)
          if (data.user) {
            const normalizedUser = { ...data.user };
            if (normalizedUser.user_uuid !== undefined) {
              normalizedUser.user_uuid = String(normalizedUser.user_uuid);
            }
            localStorage.setItem('2fa_user', JSON.stringify(normalizedUser));
          }

          return {
            success: false,
            error: data.message || '2FA setup is required',
            code: '2FA_SETUP_REQUIRED',
            session_id: data.session_id,
            user: data.user,
            setup_required: true,
          };
        }

        // Check for 2FA verification required (NEW FLOW - session_id based)
        if (data.code === '2FA_VERIFICATION_REQUIRED' || data.otp_required) {
          console.log('🔐 2FA verification required - OTP code needed');

          // Clear any old session_id first
          localStorage.removeItem('2fa_session_id');

          // Store NEW session_id temporarily for OTP verification
          if (data.session_id) {
            localStorage.setItem('2fa_session_id', data.session_id);
            console.log('🔐 Stored NEW verification session_id:', data.session_id);
          } else {
            console.error('❌ No session_id provided in 2FA_VERIFICATION_REQUIRED response');
          }

          // Store user data if provided (for display purposes only)
          if (data.user) {
            const normalizedUser = { ...data.user };
            if (normalizedUser.user_uuid !== undefined) {
              normalizedUser.user_uuid = String(normalizedUser.user_uuid);
            }
            localStorage.setItem('2fa_user', JSON.stringify(normalizedUser));
          }

          return {
            success: false,
            error: data.message || '2FA verification is required',
            code: '2FA_VERIFICATION_REQUIRED',
            session_id: data.session_id,
            user: data.user,
            otp_required: true,
          };
        }

        // Check for 2FA verification failed (can be 401 or 200 with success: false)
        if (data.code === '2FA_VERIFICATION_FAILED') {
          console.log('❌ 2FA verification failed - invalid OTP code');
          return {
            success: false,
            error: data.message || 'Invalid verification code. Please try again.',
            code: '2FA_VERIFICATION_FAILED',
            verificationRequired: true,
          };
        }

        return {
          success: false,
          error: data.message || data.error || 'Login failed',
          code: data.code,
        };
      }

      setAuthData(data);
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      // Only show generic network error for actual network failures
      return {
        success: false,
        error: error instanceof TypeError && error.message.includes('fetch')
          ? 'Network error or server unavailable'
          : 'An unexpected error occurred. Please try again.',
      };
    }
  };

  // Verify OTP with session_id (NEW 2FA FLOW)
  const verifyOtpWithSession = async (sessionId: string, otpCode: string) => {
    try {
      console.log('🔐 Verifying OTP with session_id');

      const response = await fetch(`${API_BASE_URL}/account/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          otp_code: otpCode,
        }),
      });

      // Workaround: Wrap large numeric IDs in quotes before JSON.parse to prevent precision loss
      const responseText = await response.text();

      // Check if response is HTML instead of JSON
      if (responseText.trim().startsWith('<')) {
        console.error('❌ Received HTML instead of JSON during OTP verification. API URL might be incorrect.');
        console.error('API URL:', `${API_BASE_URL}/account/2fa/verify`);
        console.error('Response preview:', responseText.substring(0, 200));
        return {
          success: false,
          error: 'Unable to connect to authentication server. Please try again.',
        };
      }

      const fixedText = responseText
        .replace(/"user_uuid":\s*(\d{15,})/g, '"user_uuid": "$1"')
        .replace(/"account_uuid":\s*(\d{15,})/g, '"account_uuid": "$1"')
        .replace(/"user_id":\s*(\d{15,})/g, '"user_id": "$1"')
        .replace(/"account_id":\s*(\d{15,})/g, '"account_id": "$1"');
      const data = JSON.parse(fixedText);

      if (!response.ok || !data.success) {
        console.error('❌ OTP verification failed:', data.error || data.message);
        return {
          success: false,
          error: data.error || data.message || 'Invalid OTP code. Please try again.',
          code: data.code,
        };
      }

      // Success - store tokens and user data
      console.log('✅ OTP verified successfully');
      console.log('🔐 Token received:', data.access_token ? 'Token present' : 'Token missing');
      console.log('🔐 Token preview:', data.access_token ? data.access_token.substring(0, 50) + '...' : 'N/A');

      // Clear temporary 2FA data
      localStorage.removeItem('2fa_session_id');
      localStorage.removeItem('2fa_user');

      // Store auth data (this sets the token in API client immediately)
      setAuthData(data);

      // Verify token was set in API client
      const { getAuthToken } = await import('../api/api');
      const tokenAfterSet = getAuthToken();
      console.log('🔍 Token verification after setAuthData:', tokenAfterSet ? 'Token set ✅' : 'Token NOT set ❌');
      console.log('🔍 localStorage token:', localStorage.getItem('access_token') ? 'Token in storage ✅' : 'Token NOT in storage ❌');

      return { success: true };
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      // Only show generic network error for actual network failures
      return {
        success: false,
        error: error instanceof TypeError && error.message.includes('fetch')
          ? 'Network error or server unavailable'
          : 'An unexpected error occurred. Please try again.',
      };
    }
  };

  // Logout
  const logout = async () => {
    console.log('🔓 Logging out...');

    try {
      // Call backend logout endpoint (optional - backend doesn't maintain session)
      await fetch(LOGOUT_ENDPOINT, {
        method: 'POST',
      });
    } catch (error) {
      console.error('❌ Logout API error:', error);
    }

    // Clear tokens from state and BOTH API clients
    setAuthToken(null);
    setRefreshToken(null);
    setApiClientToken(null);  // Also clear apiClient.ts token
    setApiClientRefreshToken(null);  // Also clear apiClient.ts refresh token
    setIsAuthenticated(false);
    setUser(null);
    setAccount(null);
    setToken(null);

    // Clear tokens (cookies + localStorage)
    clearTokens();
    // Clear other localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('account');
    localStorage.removeItem('2fa_session_id');
    localStorage.removeItem('2fa_user');
    localStorage.removeItem('admin_access_token');

    console.log('✅ Logged out successfully');
    window.location.href = getLoginUrl();
  };

  // Start impersonation session
  const startImpersonation = async (params: {
    sessionToken: string;
    sessionId: string;
    expiresAt: string;
    reason: string;
    adminToken: string;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('👤 Starting impersonation session...');
      console.log('👤 Impersonation params:', {
        sessionToken: params.sessionToken ? `${params.sessionToken.substring(0, 20)}...` : 'MISSING',
        sessionId: params.sessionId,
        expiresAt: params.expiresAt,
        reason: params.reason,
        adminToken: params.adminToken ? `${params.adminToken.substring(0, 20)}...` : 'MISSING',
      });

      // Call /me API with admin token + impersonation token to get impersonated user data
      const meUrl = `${PRIVATE_API_BASE_URL}/me`;
      const headers = {
        'Authorization': `Bearer ${params.adminToken}`,
        'X-Impersonation-Token': params.sessionToken,
        'Accept': 'application/json',
      };
      console.log('👤 Calling /me endpoint:', meUrl);
      console.log('👤 Calling /me with headers:', {
        Authorization: headers.Authorization.substring(0, 30) + '...',
        'X-Impersonation-Token': headers['X-Impersonation-Token'] ? `${headers['X-Impersonation-Token'].substring(0, 30)}...` : 'MISSING',
      });

      const response = await fetch(meUrl, {
        method: 'GET',
        headers,
      });

      console.log('👤 /me response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to fetch impersonated user data:', response.status, errorText);
        return { success: false, error: `Failed to start impersonation session: ${response.status} - ${errorText}` };
      }

      const data = await response.json();
      console.log('👤 Impersonated user data:', data);

      // Extract user and account from response (response has {user: {...}, account: {...}, ...})
      const userData = data.user || data;
      const accountData = data.account || {};

      // Build user object from /me response
      const impersonatedUser: User = {
        user_uuid: String(userData.user_uuid || userData.id || ''),
        email: userData.email || '',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        sub: userData.sub || userData.email || '',
        is_superadmin: userData.roles?.is_superadmin || data.roles?.is_superadmin || false,
        is_hrmanager: userData.roles?.is_global_hrbp || data.roles?.is_global_hrbp || false,
        is_rc_hrbp: userData.roles?.is_scoped_hrbp || data.roles?.is_scoped_hrbp || false,
        is_rc_manager: userData.roles?.is_manager || data.roles?.is_manager || false,
        is_rc_employee: userData.roles?.is_employee || data.roles?.is_employee || true,
        is_rc_executive: userData.roles?.is_executive || data.roles?.is_executive || false,
      };

      console.log('👤 Parsed impersonated user:', impersonatedUser);

      // Build account object
      const impersonatedAccount: Account = normalizeAccount({
        ...accountData,
        account_uuid: accountData.account_uuid || accountData.id || '',
        timezone: accountData.timezone,
        min_age: accountData.min_age,
      }) || {
        account_uuid: String(accountData.account_uuid || accountData.id || ''),
        name: accountData.company_name || accountData.account_name || accountData.name || '',
        domain: accountData.domain || accountData.account_domain || '',
        timezone: accountData.timezone,
        min_age: accountData.min_age,
      };

      console.log('👤 Parsed impersonated account:', impersonatedAccount);

      // Store impersonation data
      const impData: ImpersonationData = {
        sessionToken: params.sessionToken,
        sessionId: params.sessionId,
        expiresAt: new Date(params.expiresAt),
        reason: params.reason,
        adminToken: params.adminToken,
      };

      // Set state - use admin token for API calls, but show impersonated user
      setToken(params.adminToken);
      setAuthToken(params.adminToken);
      setUser(impersonatedUser);
      setAccount(impersonatedAccount);
      setIsImpersonating(true);
      setImpersonationData(impData);
      setIsAuthenticated(true);

      // Store in localStorage/cookies for page refreshes (but mark as impersonation)
      setStoredAccessToken(params.adminToken);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));
      localStorage.setItem('account', JSON.stringify(impersonatedAccount));
      const impersonationDataToStore = {
        sessionToken: params.sessionToken,
        sessionId: params.sessionId,
        expiresAt: params.expiresAt,
        reason: params.reason,
        adminToken: params.adminToken,
      };
      localStorage.setItem('impersonation_data', JSON.stringify(impersonationDataToStore));

      // Verify the data was stored
      const storedData = localStorage.getItem('impersonation_data');
      console.log('✅ Impersonation data stored:', storedData ? 'SUCCESS' : 'FAILED');
      console.log('📦 Stored impersonation_data:', storedData);
      console.log('✅ Impersonation session started successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Error starting impersonation:', error);
      return { success: false, error: 'Failed to start impersonation session' };
    }
  };

  // End impersonation session
  const endImpersonation = async () => {
    console.log('👤 Ending impersonation session...');

    // Get impersonation data from state or localStorage
    const impData = impersonationData || (() => {
      try {
        const stored = localStorage.getItem('impersonation_data');
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })();

    try {
      if (impData) {
        // Call backend to end the session
        await fetch(`${PRIVATE_API_BASE_URL.replace('/api/v1', '')}/api/v1/admin/impersonation/end`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${impData.adminToken}`,
            'X-Impersonation-Token': impData.sessionToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
      }
    } catch (error) {
      console.error('❌ Error ending impersonation session:', error);
    }

    // Clear impersonation state
    setIsImpersonating(false);
    setImpersonationData(null);
    setUser(null);
    setAccount(null);
    setToken(null);
    setIsAuthenticated(false);

    // Clear impersonation-related storage (but keep admin_access_token for admin portal)
    clearTokens();
    localStorage.removeItem('user');
    localStorage.removeItem('account');
    localStorage.removeItem('impersonation_data');

    console.log('✅ Impersonation session ended, redirecting to admin portal...');

    // Redirect to admin portal (hardcoded to production URL)
    window.location.href = `${import.meta.env.VITE_SHELL_APP_URL}/tnadmin/client`;
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      isRolesLoading,
      user,
      account,
      token,
      isImpersonating,
      impersonationData,
      loginWithGoogle,
      registerWithGoogle,
      loginWithMicrosoft,
      loginWithCredentials,
      verifyOtpWithSession,
      checkAuthMethod,
      adminCheckAuth,
      adminLoginWithCredentials,
      setAuthData,
      updateAccount,
      refreshUser,
      logout,
      startImpersonation,
      endImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
